import { Injectable } from "@angular/core";
import { Action, Select, Selector, State, StateContext } from "@ngxs/store";
import { filter, Observable, of, switchMap, take, tap } from "rxjs";
import {
  AddToCart,
  DeleteCart,
  CloseStickyCart,
  ToggleSidebarCart,
  ClearCart,
  RemoveFromCart,
  RemoveItemFromCart,
  SyncCartAfterLogin,
  SyncCartAfterCheckout} from "./cart.action";
import { CartService } from "../service/cart.service";
import { ProductCart } from "../model/cart-model";
import { CartMapper } from "../mapper/cart-mapper";
import { AccountState } from "../../account/state/account.state";
import { AuthState } from "../../auth/state/auth.state";
import { CartRequest } from "../dto/request/cart-request.interface";

export interface CartStateModel {
  id: string;
  products: ProductCart[];
  total: number;
  stickyCartOpen: boolean;
  sidebarCartOpen: boolean;
}

@State<CartStateModel>({
  name: "cart",
  defaults: {
    id: null!,
    products: [],
    total: 0,
    stickyCartOpen: false,
    sidebarCartOpen: false}})
@Injectable()
export class CartState {
  @Select(AuthState.id) userId$: Observable<string>;
  @Select(AccountState.accountId) accountId$: Observable<string>;

  constructor(private cartService: CartService) {}

  @Selector()
  static cartId(state: CartStateModel) {
    return state.id;
  }

  @Selector()
  static cartItems(state: CartStateModel) {
    return state.products;
  }

  @Selector()
  static totalItems(state: CartStateModel) {
    return state.products.length;
  }

  @Selector()
  static cartTotal(state: CartStateModel) {
    return state.products.reduce(
      (sum, product) => sum + (product.total_price || 0),
      0
    );
  }

  @Selector()
  static stickyCart(state: CartStateModel) {
    return state.stickyCartOpen;
  }

  @Selector()
  static sidebarCartOpen(state: CartStateModel) {
    return state.sidebarCartOpen;
  }

  @Action(AddToCart)
  add(ctx: StateContext<CartStateModel>, action: AddToCart) {
    const state = ctx.getState();
    const newProduct: ProductCart = action.payload;
    const addQuantity = newProduct.quantity || 1;

    const productIndex = state.products.findIndex(
      (product) =>
        product.productId === newProduct.productId &&
        product.color === newProduct.color &&
        product.size === newProduct.size
    );

    let updatedProducts = [...state.products];

    if (productIndex !== -1) {
      // Produto já existe, soma a quantidade
      updatedProducts[productIndex].quantity += addQuantity;
      updatedProducts[productIndex].total_price =
        updatedProducts[productIndex].price *
        updatedProducts[productIndex].quantity;
      updatedProducts[productIndex].total_discount_price =
        updatedProducts[productIndex].discountPrice *
        updatedProducts[productIndex].quantity;
    } else {
      // Produto não existe, adiciona com a quantidade informada
      updatedProducts.push({
        ...newProduct,
        quantity: addQuantity,
        total_price: newProduct.price * addQuantity,
        total_discount_price: newProduct.discountPrice * addQuantity});
    }

    ctx.patchState({
      ...state,
      products: updatedProducts,
      total: updatedProducts.reduce(
        (sum, product) => sum + (product.total_price || 0),
        0
      )});

    return this.persistCart(ctx.getState());
  }

  @Action(RemoveFromCart)
  removeFromCart(ctx: StateContext<CartStateModel>, action: RemoveFromCart) {
    const state = ctx.getState();
    const { productId, color, size } = action.payload;

    const productIndex = state.products.findIndex(
      (product) =>
        product.productId === productId &&
        product.color === color &&
        product.size === size
    );

    let updatedProducts = [...state.products];

    if (productIndex !== -1) {
      if (updatedProducts[productIndex].quantity > 1) {
        updatedProducts[productIndex].quantity -= 1;
        updatedProducts[productIndex].total_price =
          updatedProducts[productIndex].price *
          updatedProducts[productIndex].quantity;
        updatedProducts[productIndex].total_discount_price =
          updatedProducts[productIndex].discountPrice *
          updatedProducts[productIndex].quantity;
      } else {
        updatedProducts.splice(productIndex, 1);
      }
    }

    ctx.patchState({
      ...state,
      products: updatedProducts,
      total: updatedProducts.reduce(
        (sum, product) => sum + (product.total_price || 0),
        0
      )});

    return this.persistCart(ctx.getState());
  }

  private persistCart(state: CartStateModel): Observable<any> | void {
    if (state.id) {
      return this.cartService.updateCart(state.id, {
        cartProducts: state.products.map((product) => ({
          quantity: product.quantity,
          color: product.color,
          size: product.size,
          productStreamerId: product.productId}))});
    } else {
      sessionStorage.setItem("cart", JSON.stringify(state.products));
    }
  }

  @Action(DeleteCart)
  delete(ctx: StateContext<CartStateModel>) {
    ctx.patchState({
      id: null!,
      products: [],
      stickyCartOpen: false,
      sidebarCartOpen: false});
    sessionStorage.setItem("cart", JSON.stringify([]));
  }

  @Action(RemoveItemFromCart)
  removeItemFromCart(ctx: StateContext<CartStateModel>, action: RemoveItemFromCart) {
    const state = ctx.getState();
    const { productId, color, size } = action.payload;

    const updatedProducts = state.products.filter(
      (product) =>
        !(product.productId === productId &&
          product.color === color &&
          product.size === size)
    );

    ctx.patchState({
      ...state,
      products: updatedProducts,
      total: updatedProducts.reduce(
        (sum, product) => sum + (product.total_price || 0),
        0
      )});

    return this.persistCart(ctx.getState());
  }

  @Action(CloseStickyCart)
  closeStickyCart(ctx: StateContext<CartStateModel>) {
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      stickyCartOpen: false});
  }

  @Action(ToggleSidebarCart)
  toggleSidebarCart(
    ctx: StateContext<CartStateModel>,
    { value }: ToggleSidebarCart
  ) {
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      sidebarCartOpen: value});
  }

  @Action(ClearCart)
  clearCart(ctx: StateContext<CartStateModel>) {
    ctx.patchState({
      id: null!,
      products: [],
      total: 0,
      stickyCartOpen: false,
      sidebarCartOpen: false});
    sessionStorage.removeItem("cart");
  }

  @Action(SyncCartAfterLogin)
  syncCartAfterLogin(
    ctx: StateContext<CartStateModel>,
    _action: SyncCartAfterLogin
  ) {
    const sessionProducts: ProductCart[] = JSON.parse(
      sessionStorage.getItem("cart") || "[]"
    );

    return this.accountId$.pipe(
      filter((accountId) => !!accountId),
      take(1),
      switchMap((accountId) =>
        this.cartService.getCartByUserId(accountId).pipe(
          switchMap((response) => {
            const backendCart =
              CartMapper.fromCartResponseToCartModel(response);

            if (backendCart.id === "") {
              const cartRequest = this.toCartRequest(
                sessionProducts,
                accountId
              );
              return this.cartService.createCart(cartRequest).pipe(
                tap((newCart) => {
                  ctx.patchState({
                    id: newCart.id,
                    products: [...sessionProducts],
                    total: sessionProducts.reduce(
                      (sum, p) => sum + (p.total_price || 0),
                      0
                    )});
                  sessionStorage.removeItem("cart");
                })
              );
            } else {
              const mergedProducts = this.mergeProducts(
                backendCart.products,
                sessionProducts
              );
              const cartRequest = this.toCartRequest(mergedProducts, accountId);
              return this.cartService
                .updateCart(backendCart.id, {
                  cartProducts: cartRequest.cartProducts})
                .pipe(
                  tap(() => {
                    ctx.patchState({
                      id: backendCart.id,
                      products: mergedProducts,
                      total: mergedProducts.reduce(
                        (sum, p) => sum + (p.total_price || 0),
                        0
                      )});
                    sessionStorage.removeItem("cart");
                  })
                );
            }
          })
        )
      )
    );
  }

  @Action(SyncCartAfterCheckout)
  syncCartAfterCheckout(
    ctx: StateContext<CartStateModel>,
    _action: SyncCartAfterLogin
  ) {
    return this.accountId$.pipe(
      filter((accountId) => !!accountId),
      take(1),
      switchMap((accountId) =>
        this.cartService
          .createCart({ userId: accountId, cartProducts: [] })
          .pipe(
            tap((newCart) => {
              ctx.patchState({
                id: newCart.id,
                products: [],
                total: 0});
              sessionStorage.removeItem("cart");
            })
          )
      )
    );
  }

  private mergeProducts(
    backend: ProductCart[],
    session: ProductCart[]
  ): ProductCart[] {
    const merged = [...backend];
    session.forEach((sessionProduct) => {
      const idx = merged.findIndex(
        (p) =>
          p.productId === sessionProduct.productId &&
          p.color === sessionProduct.color &&
          p.size === sessionProduct.size
      );
      if (idx !== -1) {
        merged[idx].quantity += sessionProduct.quantity;
        merged[idx].total_price = merged[idx].price * merged[idx].quantity;
        merged[idx].total_discount_price =
          merged[idx].discountPrice * merged[idx].quantity;
      } else {
        merged.push(sessionProduct);
      }
    });
    return merged;
  }

  private toCartRequest(products: ProductCart[], userId?: string): CartRequest {
    return {
      userId,
      cartProducts: products.map((product) => ({
        quantity: product.quantity,
        color: product.color,
        size: product.size,
        productStreamerId: product.productId}))};
  }
}
